import { TimeTypes } from '@/config/map.conf';
import * as api from '@/api';
import store from '@/store';

export function getStringLength(str) {
    let length = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        if ((char >= 0x0001 && char <= 0x007e) || (char >= 0xff60 && char <= 0xff9f)) {
            length++;
        } else {
            length += 2;
        }
    }
    return length;
}

export function formatTimeStr(time: number, unit: string) {
    let result = '';
    if (unit === 'HOUR') {
        const day = Math.floor(time / 24);
        const hour = time % 24;
        if (day > 0) {
            result = `${day}天`;
        }
        if (hour > 0) {
            result += `${hour}小时`;
        }
    } else if (unit === 'MINUTE') {
        const day = Math.floor(time / (24 * 60));
        let hour = Math.floor(time / 60);
        let min = time % (24 * 60);
        if (day > 0) {
            result = `${day}天`;
            hour = Math.floor((time % (24 * 60)) / 60);
        }
        if (hour > 0) {
            result += `${hour}小时`;
            min = time % 60;
        }
        if (min > 0) {
            result += `${min}分钟`;
        }
    } else {
        result = time + TimeTypes[unit];
    }
    return result;
}
// 批量注册组件
export const importComponents: (componentsField: any, components: CommonTypes.mapObject) => void = async (
    componentsField: any,
    components: CommonTypes.mapObject
) => {
    componentsField.keys().forEach((url: string) => {
        const name = url.substring(2, url.length - 4);
        components[name] = componentsField(url).default;
    });
};
// 首字母大写
export function firstUpperCase(str: string) {
    return str.toLowerCase().replace(/( |^)[a-z]/g, (L: string) => L.toUpperCase());
}
// 当遇到数组时 先将Vuex中不存在的用户名 获取到并提交至Vuex
export async function submitUserArrToVuex(currentUserDisplay: CommonTypes.userDisplayItem[], username: string[]) {
    if (!currentUserDisplay || !username) {
        return;
    }
    const vuexArr = currentUserDisplay.map(currentUser => {
        return currentUser.username;
    });
    const newVuexArr = currentUserDisplay.concat();
    if (username instanceof Array) {
        // 求两个数组username的差集
        const unionSet = new Set([...vuexArr, ...username]);
        const diffSet = new Set(Array.from(unionSet).filter(user => !new Set(vuexArr).has(user)));
        const diffArr = Array.from(diffSet);
        // 请求不存在的username 并保存到vuex中
        if (diffArr.length > 0) {
            const res = await api.ctiApi.searchDisplayNameList(diffArr);
            const result = res.data || {};
            for (const user in result) {
                (result[user] as any).username = user;
                newVuexArr.push(result[user] as any);
            }
            store.commit('GET_USER_DISPLAY', newVuexArr);
        }
    }
}
export function highlightMatch(val, str) {
    const reg = new RegExp(val, 'gi');
    return str.replace(reg, `<span style='color: #FF8800'>${val}</span>`);
}

/**
 * 获取当前时区与UTC时区的偏差（以毫秒为单位).
 */
export function getCurrentTimezoneOffsetMs(): number {
    // getTimezoneOffset()返回的是以分钟为单位的偏差，且是UTC时区减去本地时区的结果
    // 因此，我们需要将其乘以-1来获取本地时区相对于UTC的偏差，然后将分钟转换为毫秒
    const offsetMinutes = new Date().getTimezoneOffset();
    const offsetMs = offsetMinutes * -60 * 1000;
    return offsetMs;
}

/**
 * 方法装饰器 `@Once`，使被装饰的方法最多执行一次.
 */
export function Once<T extends (...args: any[]) => any>(
    _target: any,
    _propertyKey: string,
    descriptor: TypedPropertyDescriptor<T>
): TypedPropertyDescriptor<T> | void {
    const originalMethod = descriptor.value!;
    let res: ReturnType<T>;
    let hasRun = false;

    const newMethod = function (this: any, ...args: Parameters<T>): ReturnType<T> {
        if (!hasRun) {
            hasRun = true;
            res = originalMethod.apply(this, args);
        }
        return res;
    };

    descriptor.value = newMethod as T;
    return descriptor;
}

/**
 * 方法装饰器 `@Debounce`，使被装饰的方法在指定时间内只执行最后一次.
 */
export function Debounce<T extends (...args: any[]) => any>(delayMs: number) {
    return function (_target: any, _propertyKey: string, descriptor: TypedPropertyDescriptor<T>): void {
        const originalMethod = descriptor.value!;
        let timer: ReturnType<typeof setTimeout> | null = null;

        const newMethod = function (this: any, ...args: Parameters<T>): void {
            if (timer) {
                clearTimeout(timer);
            }
            timer = setTimeout(() => {
                originalMethod.apply(this, args);
            }, delayMs);
        };

        descriptor.value = newMethod as T;
    };
}

export function Throttle<T extends (...args: any[]) => any>(delayMs: number) {
    return function (_target: any, _propertyKey: string, descriptor: TypedPropertyDescriptor<T>): void {
        const originalMethod = descriptor.value!;
        let timer: ReturnType<typeof setTimeout> | null = null;

        const newMethod = function (this: any, ...args: Parameters<T>): void {
            if (!timer) {
                timer = setTimeout(() => {
                    originalMethod.apply(this, args);
                    timer = null;
                }, delayMs);
            }
        };

        descriptor.value = newMethod as T;
    };
}

export function groupBy<V, K extends PropertyKey>(arr: V[], key: (item: V) => K): Map<K, V[]> {
    const res = new Map<K, V[]>();
    arr.forEach(item => {
        const k = key(item);
        if (!res.has(k)) res.set(k, []);
        res.get(k)!.push(item);
    });
    return res;
}

export function topoSortDirected<V extends PropertyKey>(
    vertices: V[],
    depFn: (value: V, index: number) => V[]
): {
    hasCycle: boolean;
    topoOrder: V[];
} {
    const inDegree = new Map<V, number>();
    const adjList = new Map<V, V[]>();
    vertices.forEach(v => {
        inDegree.set(v, 0);
        adjList.set(v, []);
    });
    vertices.forEach((v, i) => {
        depFn(v, i).forEach(d => {
            adjList.get(d)!.push(v);
            inDegree.set(v, inDegree.get(v)! + 1);
        });
    });

    const queue: V[] = [];
    vertices.forEach(v => {
        if (inDegree.get(v) === 0) {
            queue.push(v);
        }
    });
    const topoOrder: V[] = [];
    while (queue.length) {
        const cur = queue.shift()!;
        topoOrder.push(cur);
        adjList.get(cur)!.forEach(next => {
            inDegree.set(next, inDegree.get(next)! - 1);
            if (inDegree.get(next) === 0) {
                queue.push(next);
            }
        });
    }

    const hasCycle = topoOrder.length !== vertices.length;

    return {
        hasCycle,
        topoOrder
    };
}
